import React, { useState } from "react";

import { Formik } from "formik";
import * as Yup from "yup";
import FormikFormLine from "../../../../formik-form-components/FormikFormLine";
import FormikSelect from "../../../../formik-form-components/FormikSelect";
import FormikNumberInput from "../../../../formik-form-components/FormikNumberInput";
import ContainerFormButtons from "./ContainerFormButtons";
import { FormContainer } from "../../../../../constants/Styles";
import useFetchAndTrace from "../../../../../utils/hooks/fetchWithTrace";
import settings from "../../../../../constants/settings";
import { baseContainerSchema, ContainerDetailsFormValues } from "./constants";
import { USD } from "../../../../../constants/orders";
import { FieldTypes } from "../../../../formik-form-components/FormikFormLine/constants";
import { Part, PlacementType } from "../../../../../reducers/models/parts";
import Button from "../../../../common/Button";
import { ButtonTypes } from "../../../../common/Button/constants";
import { InventoryResponse } from "../../../../../reducers/componentSearch";
import PriceField from "./PriceField";
import BinBarcodeField from "./BinBarcodeField";
import {
  useCustomAlert,
  useCustomConfirm,
} from "../../../../../utils/hooks/alerts";
import useContainerHistorySummary from "../../../../../utils/hooks/inventory/containerHistory";
import SelectUser from "../../../../SelectUser";
import { useFlashPCBSettings } from "../../../../../utils/hooks/commonly-stored/flashPCBSettings";
import { InventoryBin } from "../../../../internal/parts/Bins/inventoryBinTypes";
import { UserSummary } from "../../../../../reducers/models/users";
import { usePrintBarcode } from "../../../../../utils/hooks/inventory/printBarcode";
import { MAX_CONTAINER_QUANTITY } from "./UpdateContainerQuantity";
import { FLASHPCB_BIN_BARCODE_REGEX } from "../../../../internal/parts/InventoryBinForm";
import FormikRadioOptions from "../../../../formik-form-components/FormikRadioOptions";
import FlashPCBExpandable from "../../../../common/FlashPCBCollapsible";
import {
  PART_NOT_ON_BOM_CONFIRM,
  PART_NOT_ON_BOM_CONFIRM_TEXT,
  PART_NOT_ON_BOM_ERROR,
} from "../../../../../constants/errors";

export const CONTAINER_TYPE_OPTIONS = {
  reel: { value: "reel", label: "Reel" },
  tube: { value: "tube", label: "Tube" },
  tray: { value: "tray", label: "Tray" },
  cut_tape: { value: "cut_tape", label: "Cut tape" },
  loose_parts: { value: "loose_parts", label: "Loose part(s)" },
};

export type ContainerType = keyof typeof CONTAINER_TYPE_OPTIONS;

export enum MSLLevel {
  SIX = "6",
  FIVE_A = "5a",
  FIVE = "5",
  FOUR = "4",
  THREE = "3",
  TWO_A = "2a",
  TWO = "2",
  ONE = "1",
}

interface GenerateStickerFormValues {
  manufacturersPartNumber?: string;
  manufacturer?: string;
  description?: string;
  consignmentPartName?: string;
  notes?: string;
  user?: string;
}

const partStickerSchema = Yup.object().shape({
  manufacturersPartNumber: Yup.string().max(50, "Too long!"),
  manufacturer: Yup.string(),
  description: Yup.string().max(100, "Too long!"),
});

const schema = baseContainerSchema.shape({
  lastCountedAmount: Yup.number()
    .integer("Last counted amount must be an integer")
    .required("Last counted amount is required")
    .min(
      1,
      "Last counted amount must be greater than 0 when adding a container",
    )
    .max(
      MAX_CONTAINER_QUANTITY,
      `Quantity cannot exceed ${MAX_CONTAINER_QUANTITY.toLocaleString()}`,
    )
    .when("flashpcbBinBarcode", {
      is: (value: string | null) => !value,
      then: (schema) =>
        schema.max(
          0,
          "If you are omitting a bin for this part then you must zero out the count",
        ),
    }),
  flashpcbBinBarcode: Yup.string()
    .matches(new RegExp(FLASHPCB_BIN_BARCODE_REGEX))
    .required("Bin barcode is required when adding a container"),
  containerType: Yup.mixed()
    .oneOf(Object.values(CONTAINER_TYPE_OPTIONS).map((option) => option.value))
    .required("Container type is required"),
});

const AddNewContainer = ({
  part,
  partId,
  onFinishedEditing,
  onCancel,
  inventoryResponse,
  inventoryBin,
  showInfoSticker,
  defaultQuantity = 0,
  defaultDateCode = "",
  defaultCountryCode = "",
  defaultLotNumber = "",
  user,
}: {
  part: Part;
  partId: string;
  onFinishedEditing: () => void;
  onCancel: () => void;
  inventoryResponse?: InventoryResponse;
  inventoryBin?: InventoryBin;
  showInfoSticker?: boolean;
  defaultQuantity?: number;
  defaultDateCode?: string;
  defaultCountryCode?: string;
  defaultLotNumber?: string;
  user?: UserSummary | null;
}) => {
  const customAlert = useCustomAlert();
  const customConfirm = useCustomConfirm();
  const { fetchHistory } = useContainerHistorySummary();
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(
    user ? user : null,
  );
  const fetchAndTrace = useFetchAndTrace();
  const flashPCBSettings = useFlashPCBSettings();
  const uniqueVendors: string[] = Array.from(
    new Set(
      inventoryResponse?.virtual_inventory.sellers_inventory.map(
        (part) => part.seller,
      ),
    ),
  );
  const barcodeError = () => {
    customAlert("Error creating duplicate barcode label.");
  };

  const { createBarcode } = usePrintBarcode(barcodeError);

  const vendorOptions = uniqueVendors.map((vendor) => ({
    label: vendor,
    value: vendor,
  }));

  const errorHandler = () => {
    setLoading(false);
    customAlert("Failed to add container");
  };

  const makeAddContainerHandler = (retryAllowingPartNotOnBom: () => void) => {
    return async (response: Response) => {
      if (response.ok) {
        fetchHistory();
        onFinishedEditing();
      } else if (response.status == 400) {
        const { detail } = (await response.json()) as { detail: string };
        if (detail === PART_NOT_ON_BOM_ERROR) {
          customConfirm(
            PART_NOT_ON_BOM_CONFIRM,
            retryAllowingPartNotOnBom,
            undefined,
            { confirmText: PART_NOT_ON_BOM_CONFIRM_TEXT },
          );
        } else {
          customAlert(detail);
        }
      } else {
        throw Error("failed to add new container to database.");
      }
      setLoading(false);
    };
  };

  const onSubmit = (values: ContainerDetailsFormValues) => {
    const doSubmit = (allowPartNotOnBom: boolean) => {
      setLoading(true);
      void fetchAndTrace(
        `${settings.api_url}/inventory/containers`,
        makeAddContainerHandler(() => {
          doSubmit(true);
        }),
        errorHandler,
        {
          body: JSON.stringify({
            part_id: partId,
            allow_part_not_on_bom: allowPartNotOnBom,
            container_type: values.containerType,
            price_info: {
              unit_price: values.unitPrice,
              quantity: 1,
              currency: USD,
            },
            last_counted_amount: values.lastCountedAmount,
            order_date: values.orderDate || null,
            nickname: values.nickname || null,
            notes: values.notes || null,
            moisture_sensitivity_level: values.moistureSensitivityLevel || null,
            flashpcb_container_barcode: values.flashpcbContainerBarcode,
            flashpcb_bin_barcode: values.flashpcbBinBarcode,
            vendor: values.vendor,
            part_allocations: values.allocations,
            spent_part_allocations: values.spentAllocations,
            consignment_container_owner_id: selectedUser?.id || null,
            date_code: values.dateCode || null,
            country_code: values.countryCode || null,
            lot_number: values.lotNumber || null,
            reel_width_mm: values.reelWidthMM || null,
            reel_pitch_mm: values.reelPitchMM || null,
            part_placement_type: values.partPlacementType || null,
          }),
          method: "POST",
        },
      );
    };
    if (!values.flashpcbBinBarcode) {
      customConfirm(
        "You are sure you want to add a part without putting it in a bin?",
        () => {
          doSubmit(false);
        },
      );
    } else {
      doSubmit(false);
    }
  };

  const stickerHandler = async (response: Response) => {
    if (response.ok) {
      const blob = await response.blob();
      window.open(URL.createObjectURL(blob));
    } else if (response.status == 400) {
      const { detail } = (await response.json()) as { detail: string };
      customAlert(detail);
    } else {
      throw new Error("Unable to generate custom info sticker");
    }
    setLoading(false);
  };

  const handleGenerateInfoSticker = (values: GenerateStickerFormValues) => {
    const url = part
      ? `${settings.api_url}/parts/info-sticker`
      : `${settings.api_url}/consignment-parts/info-sticker`;
    setLoading(true);
    void fetchAndTrace(url, stickerHandler, errorHandler, {
      body: JSON.stringify({
        mpn: values.manufacturersPartNumber,
        manufacturer: values.manufacturer,
        desc: values.description,
        name: values.consignmentPartName,
        notes: values.notes,
        user: values.user,
      }),
      method: "POST",
    });
  };

  return (
    <>
      {part && showInfoSticker && (
        <Formik
          initialValues={{
            manufacturersPartNumber: part.name,
            manufacturer: part.manufacturer || "",
            description: part.short_description,
          }}
          onSubmit={handleGenerateInfoSticker}
          validationSchema={partStickerSchema}
          validationOnChange={true}
        >
          {({ handleSubmit, isValid }) => (
            <FormContainer onSubmit={handleSubmit}>
              <FormikFormLine
                name={"manufacturersPartNumber"}
                label={"Manufacturer's Part Number"}
                isRequired={false}
              />
              <FormikFormLine
                name={"manufacturer"}
                label={"Manufacturer"}
                isRequired={false}
              />
              <FormikFormLine
                name={"description"}
                label={"Description"}
                isTextArea={true}
                isRequired={false}
              />
              <Button
                disabled={!isValid || loading}
                label={"Generate custom info sticker"}
                type={ButtonTypes.SUBMIT}
              />
            </FormContainer>
          )}
        </Formik>
      )}
      <Formik
        initialValues={{
          containerType: "",
          lastCountedAmount: defaultQuantity,
          nickname: "",
          notes: "",
          unitPrice: 0,
          orderDate: "",
          vendor: "",
          moistureSensitivityLevel: "1",
          flashpcbContainerBarcode: "",
          flashpcbBinBarcode: inventoryBin?.flashpcb_bin_barcode || "",
          allocations: [],
          spentAllocations: [],
          dateCode: defaultDateCode,
          countryCode: defaultCountryCode,
          lotNumber: defaultLotNumber,
          reelWidthMM: part.reel_details.reel_width_mm,
          reelPitchMM: part.reel_details.reel_pitch_mm,
          partPlacementType: part.placement_type || "",
        }}
        onSubmit={onSubmit}
        validationSchema={schema}
      >
        {({ handleSubmit, isValid, values }) => {
          return (
            <FormContainer>
              <SelectUser
                selectedUserId={selectedUser?.id || null}
                label={"This container is consigned and reserved for use by"}
                setSelectedUser={setSelectedUser}
                isClearable
              />
              <FormikFormLine
                autoFocus
                name="flashpcbContainerBarcode"
                label="FlashPCB Container Barcode"
                isRequired={true}
              />
              <Button
                label="Print additional label"
                onClick={() => createBarcode(values.flashpcbContainerBarcode)}
                disabled={values.flashpcbContainerBarcode.length === 0}
              />
              <FormikRadioOptions
                name={"containerType"}
                options={Object.values(CONTAINER_TYPE_OPTIONS)}
                label={"Container type:"}
              />
              <BinBarcodeField
                binBarcode={values.flashpcbBinBarcode}
                isRequired
              />
              <FormikNumberInput
                name={"lastCountedAmount"}
                label={"Last counted amount"}
                min={0}
              />
              <PriceField partId={partId} updatePrice />
              <FormikSelect
                name={"reelWidthMM"}
                label={"Reel width (mm)"}
                options={
                  flashPCBSettings?.reel_size_options.reel_width_mm.map(
                    (width) => ({ label: `${width} mm`, value: width }),
                  ) || []
                }
              />
              <FormikSelect
                name={"reelPitchMM"}
                label={"Reel pitch (mm)"}
                options={
                  flashPCBSettings?.reel_size_options.reel_pitch_mm.map(
                    (pitch) => ({ label: `${pitch} mm`, value: pitch }),
                  ) || []
                }
              />
              <FormikSelect
                name={"partPlacementType"}
                label={"Part placement type"}
                options={Object.values(PlacementType).map((option) => ({
                  label: option,
                  value: option,
                }))}
              />
              <FormikSelect
                name={"moistureSensitivityLevel"}
                label={"Moisture sensitivity level"}
                options={Object.values(MSLLevel).map((msl) => ({
                  value: msl,
                  label: msl,
                }))}
              />
              <FlashPCBExpandable
                key={values.flashpcbContainerBarcode}
                renderTrigger={(collapsibleOpen) =>
                  collapsibleOpen
                    ? `Hide optional container details`
                    : `See optional container details`
                }
              >
                <FormikFormLine
                  name="nickname"
                  label="Nickname"
                  isRequired={false}
                />
                <FormikSelect
                  name={"vendor"}
                  label={"Vendor"}
                  options={vendorOptions}
                />
                <FormikFormLine name={"dateCode"} label={"Date code"} />
                <FormikFormLine name={"countryCode"} label={"Country code"} />
                <FormikFormLine name={"lotNumber"} label={"Lot number"} />
                <FormikFormLine
                  name={"orderDate"}
                  label={"Order date"}
                  type={FieldTypes.date}
                />
                <FormikFormLine
                  name={"notes"}
                  label={"Notes"}
                  isTextArea
                  isRequired={false}
                />
              </FlashPCBExpandable>
              <ContainerFormButtons
                onCancel={onCancel}
                loading={loading}
                isDisabled={!isValid}
                onSubmit={handleSubmit}
              />
            </FormContainer>
          );
        }}
      </Formik>
    </>
  );
};

export default AddNewContainer;
